import * as React from 'react'
import * as ReactDOM from 'react-dom'

import {
  Avatar,
  Box,
  Text,
  useCallback,
  useUnsafeCallback,
  useUnsafeMemo,
} from '@factorialco/deprecated-design-system'
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  MenuTextMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin'
import { $createTextNode, $getSelection, LexicalEditor, TextNode } from 'lexical'
import { debounce } from 'lodash'

import { $createMentionNode } from 'modules/core/components/TextEditor/nodes/MentionNode'
import buildUrl from 'modules/core/lib/buildUrl'

import './index.styles.css'
/*
This new version of the Mentions plugin uses debounced search and accesses.
We added a new version instead of updating the existing one in order to keep the original code for current uses.
*/

const PUNCTUATION = '\\.,\\+\\*\\?\\$\\@\\|#{}\\(\\)\\^\\-\\[\\]\\\\/!%\'"~=<>_:;'
const NAME = '\\b[A-Z][^\\s' + PUNCTUATION + ']'

const DocumentMentionsRegex = {
  NAME,
  PUNCTUATION,
}

const PUNC = DocumentMentionsRegex.PUNCTUATION

const TRIGGERS = ['@'].join('')

// Chars we expect to see in a mention (non-space, non-punctuation).
const VALID_CHARS = '[^' + TRIGGERS + PUNC + '\\s]'

// Non-standard series of chars. Each series must be preceded and followed by
// a valid char.
const VALID_JOINS =
  '(?:' +
  '\\.[ |$]|' + // E.g. "r. " in "Mr. Smith"
  ' |' + // E.g. " " in "Josh Duck"
  '[' +
  PUNC +
  ']|' + // E.g. "-' in "Salier-Hellendag"
  ')'

const LENGTH_LIMIT = 75

const AtSignMentionsRegex = new RegExp(
  '(^|\\s|\\()(' +
    '[' +
    TRIGGERS +
    ']' +
    '((?:' +
    VALID_CHARS +
    VALID_JOINS +
    '){0,' +
    LENGTH_LIMIT +
    '})' +
    ')$'
)

// 50 is the longest alias length limit.
const ALIAS_LENGTH_LIMIT = 50

// Regex used to match alias.
const AtSignMentionsRegexAliasRegex = new RegExp(
  '(^|\\s|\\()(' +
    '[' +
    TRIGGERS +
    ']' +
    '((?:' +
    VALID_CHARS +
    '){0,' +
    ALIAS_LENGTH_LIMIT +
    '})' +
    ')$'
)

export interface MentionedAccess {
  id: string
  employeeId?: string
  fullName: string
  avatar: string
}

function checkForAtSignMentions(text: string, minMatchLength: number): MenuTextMatch | null {
  let match = AtSignMentionsRegex.exec(text)

  if (match === null) {
    match = AtSignMentionsRegexAliasRegex.exec(text)
  } else {
    // The strategy ignores leading whitespace but we need to know it's
    // length to add it to the leadOffset
    const maybeLeadingWhitespace = match[1] || []
    const matchingString = match[3] || ''

    if (matchingString.length >= minMatchLength) {
      return {
        leadOffset: match.index + maybeLeadingWhitespace?.length,
        matchingString,
        replaceableString: match[2] || '',
      }
    }
  }
  return null
}

class MentionMenuOption extends MenuOption {
  name: string
  id: string
  avatar: JSX.Element
  employeeId?: string

  constructor(access: MentionedAccess, avatar: JSX.Element) {
    super(access.fullName)
    this.name = access.fullName
    this.id = access.id
    this.employeeId = access.employeeId
    this.avatar = avatar
  }
}

function MentionsTypeaheadMenuItem({
  isSelected,
  onClick,
  onMouseEnter,
  option,
}: {
  index: number
  isSelected: boolean
  onClick: () => void
  onMouseEnter: () => void
  option: MentionMenuOption
}) {
  return (
    <Box
      key={option.key}
      role="option"
      flexDirection="row"
      alignItems="center"
      paddingY="s6"
      paddingX="s16"
      outline="none"
      cursor="pointer"
      gap="s8"
      background={isSelected ? 'grey200' : undefined}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
    >
      {option.avatar}
      <Text.Normal size="200">{option.name}</Text.Normal>
    </Box>
  )
}

interface Props {
  editor: LexicalEditor
  onMentionQueryStringChanged: (queryString: string) => Promise<MentionedAccess[]> | undefined
}

function DebouncedMentionsPlugin({
  editor,
  onMentionQueryStringChanged,
}: Props): JSX.Element | null {
  const [mentionableAccesses, setMentionableAccesses] = React.useState<MentionedAccess[]>([])

  const handleQueryChange = useUnsafeCallback(
    debounce(async (queryString: string | null) => {
      if (queryString !== null) {
        const accesses = await onMentionQueryStringChanged(queryString)
        setMentionableAccesses(accesses ?? [])
      }
    }, 300),
    []
  )

  const options = useUnsafeMemo(() => {
    return mentionableAccesses.map(
      (result) =>
        new MentionMenuOption(
          result,
          <Avatar size="s24" url={buildUrl(result.avatar)} label={result.fullName} />
        )
    )
  }, [mentionableAccesses])

  const onSelectOption = useUnsafeCallback(
    (selectedOption: MentionMenuOption, nodeToReplace: TextNode | null, closeMenu: () => void) => {
      editor.update(() => {
        const mentionNode = $createMentionNode(selectedOption)

        if (nodeToReplace) {
          nodeToReplace.replace(mentionNode)
        }
        // add @ before the mentioned access name
        const mentionText = `@${selectedOption.name}`
        mentionNode.setTextContent(mentionText)
        // highlight the access name text
        mentionNode.select()
        // add a space after the mentioned access name
        $getSelection()?.insertNodes([$createTextNode(' ')])

        closeMenu()
      })
    },
    [editor]
  )

  const checkForMentionMatch = useCallback((text: string) => checkForAtSignMentions(text, 0), [])

  if (!editor.isEditable()) {
    return null
  }

  return (
    <LexicalTypeaheadMenuPlugin<MentionMenuOption>
      onQueryChange={handleQueryChange}
      onSelectOption={onSelectOption}
      triggerFn={checkForMentionMatch}
      options={options}
      menuRenderFn={(
        anchorElementRef,
        { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }
      ) => {
        const menu = anchorElementRef.current

        if (menu && options.length > 0) {
          return ReactDOM.createPortal(
            <div
              className={'plugins_commentsMentionPopover'}
              style={{ top: menu.offsetTop, left: menu.offsetLeft }}
            >
              <Box
                component="ul"
                paddingY="s12"
                borderRadius={{ all: 'abs008' }}
                background="grey000White"
                boxShadow="s100"
                overflowY="auto"
                maxHeight="s240"
                minWidth="s300"
                position="absolute"
                style={{
                  bottom: '0',
                  transform: 'translateX(-50%)',
                }}
              >
                {options.map((option, i: number) => (
                  <MentionsTypeaheadMenuItem
                    index={i}
                    isSelected={selectedIndex === i}
                    onClick={() => {
                      setHighlightedIndex(i)
                      selectOptionAndCleanUp(option)
                    }}
                    onMouseEnter={() => {
                      setHighlightedIndex(i)
                    }}
                    key={option.key}
                    option={option}
                  />
                ))}
              </Box>
            </div>,
            document.body
          )
        }

        return null
      }}
    />
  )
}

export default DebouncedMentionsPlugin
